/** SUNTRONIX ONE — CRM Pipeline Board (management).
 *  Live value-by-stage pipeline built from the zero-touch auto Opportunities.
 *  Filter by owner / customer / dealer. Cards open the source quote. */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { TrendingUp, Search, RefreshCw, Filter } from "lucide-react";

const fmtINR = (n) => n == null ? "—" :
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0, notation: "compact" }).format(n);
const fmtFull = (n) => n == null ? "—" :
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);

const STAGE_TONE = {
  QUOTE_PREPARATION: "border-slate-200 bg-slate-50", QUOTE_SENT: "border-blue-200 bg-blue-50",
  FOLLOW_UP: "border-amber-200 bg-amber-50", REVISION: "border-orange-200 bg-orange-50",
  WON: "border-emerald-200 bg-emerald-50", WON_PO_RECEIVED: "border-teal-200 bg-teal-50",
  ORDERED: "border-indigo-200 bg-indigo-50", LOST: "border-rose-200 bg-rose-50",
  CANCELLED: "border-slate-200 bg-slate-100", EXPIRED: "border-slate-200 bg-slate-50",
};

export default function CrmPipelineBoard() {
  const [data, setData] = useState(null);
  const [owner, setOwner] = useState("");
  const [customer, setCustomer] = useState("");
  const [dealerOnly, setDealerOnly] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/crm-auto/pipeline", {
        params: { owner: owner || undefined, customer: customer || undefined, dealer_only: dealerOnly || undefined },
      });
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load pipeline"); }
    finally { setBusy(false); }
  }, [customer, dealerOnly, owner]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dealerOnly, load]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [owner, customer]);

  const totals = data?.totals;
  const columns = useMemo(() => (data?.columns || []).filter((c) => c.count > 0 || ["QUOTE_PREPARATION", "QUOTE_SENT", "WON", "ORDERED"].includes(c.stage)), [data]);

  return (
    <div className="max-w-[1500px] mx-auto px-4 py-5" data-testid="crm-pipeline-page">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <TrendingUp className="h-5 w-5 text-indigo-600" />
        <h1 className="text-lg font-semibold text-slate-900">Sales Pipeline</h1>
        <span className="text-[11px] text-slate-500">Auto-built from quotations — no manual maintenance</span>
        <Button variant="outline" size="sm" className="ml-auto" onClick={load} disabled={busy} data-testid="pipeline-refresh">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* Summary tiles */}
      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="pipeline-totals">
          {[["Open pipeline", totals.open_value, "text-slate-900", `${totals.open_count} open`],
            ["Won", totals.won_value, "text-emerald-700", null],
            ["Ordered", totals.ordered_value, "text-indigo-700", null],
            ["Lost / cancelled", totals.lost_value, "text-rose-700", null],
            ["Opportunities", null, "text-slate-900", `${totals.total_count}`]].map(([label, val, tone, sub]) => (
            <div key={label} className="border border-slate-200 rounded-lg bg-white p-3">
              <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
              <div className={`text-xl font-semibold ${tone}`} title={val != null ? fmtFull(val) : ""}>
                {val != null ? fmtINR(val) : sub}
              </div>
              {val != null && sub && <div className="text-[11px] text-slate-500">{sub}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Filter className="h-4 w-4 text-slate-400" />
        <select value={owner} onChange={(e) => setOwner(e.target.value)} data-testid="pipeline-owner-filter"
          className="h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white">
          <option value="">All owners</option>
          {(data?.owners || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <div className="relative w-64">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={customer} onChange={(e) => setCustomer(e.target.value)} className="pl-9 h-9"
            placeholder="Filter by customer…" data-testid="pipeline-customer-filter" />
        </div>
        <button onClick={() => setDealerOnly((v) => !v)} data-testid="pipeline-dealer-filter"
          className={`text-[12px] rounded-full px-3 py-1.5 border ${dealerOnly ? "bg-indigo-600 text-white border-indigo-600" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
          Dealers only
        </button>
      </div>

      {/* Board */}
      {!data ? (
        <div className="text-sm text-slate-400 py-10 text-center">Loading pipeline…</div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-4" data-testid="pipeline-board">
          {columns.map((col) => (
            <div key={col.stage} className="flex-shrink-0 w-72" data-testid={`pipeline-col-${col.stage}`}>
              <div className={`rounded-t-lg border px-3 py-2 ${STAGE_TONE[col.stage] || "border-slate-200 bg-slate-50"}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[12px] font-semibold text-slate-700">{col.label}</span>
                  <span className="text-[11px] text-slate-500 bg-white/70 rounded-full px-1.5">{col.count}</span>
                </div>
                <div className="text-[13px] font-semibold text-slate-900 mt-0.5" title={fmtFull(col.value)}>{fmtINR(col.value)}</div>
              </div>
              <div className="border border-t-0 border-slate-200 rounded-b-lg bg-white p-2 space-y-2 min-h-[120px] max-h-[62vh] overflow-y-auto">
                {col.items.length === 0 ? (
                  <div className="text-[12px] text-slate-300 text-center py-4">No opportunities</div>
                ) : col.items.map((o) => (
                  <button key={o.id} onClick={() => o.source_quote_id && navigate(`/commercial/crm/quotations?open=${o.source_quote_id}`)}
                    data-testid={`pipeline-card-${o.id}`}
                    className="w-full text-left border border-slate-100 rounded-md px-2.5 py-2 hover:border-indigo-300 hover:bg-indigo-50/40 transition-colors">
                    <div className="text-[12px] font-medium text-slate-800 truncate">{o.customer_name || "Customer"}</div>
                    <div className="text-[11px] text-slate-500 truncate">{o.opportunity_name}</div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-[12px] font-semibold text-slate-900">{fmtINR(o.amount_inr)}</span>
                      <span className="text-[10px] text-slate-400 font-mono">{o.source_quote_code}</span>
                    </div>
                    {o.owner && <div className="text-[10px] text-slate-400 mt-0.5">{o.owner}</div>}
                  </button>
                ))}
                {col.count > col.items.length && (
                  <div className="text-[11px] text-slate-400 text-center pt-1">+{col.count - col.items.length} more</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
